import Note from "../models/soundslice/Note";
import NotePath from "../models/soundslice/NotePath";
import {logger} from "../services/log/logger";

/**
 * Create Note models based on data sent from Soundslice.
 * @param notes - typically the ssGetNotes arg
 */
export function createNotesFromApiResponse(notes: any){
  let noteModels: Note[] = notes.map(createNoteFromApiResponse);
  noteModels = noteModels.sort((a, b) => a.offset - b.offset);
  // console.log(`note sorted:`, JSON.stringify(noteModels));
  //soundslice doesn't provide a note duration that represents note ties, so we need to calculate the duration ourselves.
  createDurationsForNoteTies(noteModels);
  setHandForNotes(noteModels);
  return noteModels;
}

function setHandForNotes(notes: Note[]){
  const {tracks, clefs} = getTracksAndClefs(notes);
  //if there are two track/staffs that are bass clef types, track 0 (top staff) would be considered right hand, and track 1 (bottom staff) would be considered left hand.
  // The same would apply when both tracks are treble
  for(let note of notes){
    if(clefs.length === 1){ //only 1 clef in the sheet music
      if(tracks.length >= 2){
        note.hand = note.path.trackIndex === 0 ? 'right hand' : 'left hand';
      }else{
        note.hand = note.clef === 'bass' ? 'left hand' : 'right hand';
      }
    }else{
      note.hand = note.clef === 'bass' ? 'left hand' : 'right hand';
    }
  }
}

function getTracksAndClefs(notes: Note[]){
  const tracks = new Set<number>();
  const clefs = new Set<string>();
  for(let note of notes){
    tracks.add(note.path.trackIndex);
    clefs.add(note.clef);
  }

  return {clefs: Array.from(clefs), tracks: Array.from(tracks)};
}

/**
 * Creates a Note from an item in the ssGetNotes array.
 */
function createNoteFromApiResponse({duration, fret: tabFretNumber, lfinger: leftHandFinger, midi, offset, path, string: tabStringNumber, tieEnd, clef: clefString, color}: any): Note{
  const clef = clefString === 'G2' ? 'treble' : 'bass';
  const notePath = createNotePathFromApiResponse(path);
  return new Note({duration, tabFretNumber, leftHandFinger, midi, offset, path: notePath, tabStringNumber, tieEnd, clef, color: color || "#000000"});
}

/**
 * Creates a NotePath from the path property of an item in the ssGetNotes array.
 */
function createNotePathFromApiResponse([barIndex, trackIndex, voiceIndex, beatIndex, noteIndex]: any){
  return new NotePath({noteIndex, beatIndex, voiceIndex, trackIndex, barIndex});
}

/**
 * When a note is flagged as tieEnd = true, we won't play that note (see notesPlaybackScheduler), and instead play the previous note for the duration of both notes combined.
 * Notes can be tied between bars, so look at previous notes until you get to the same note (midi number)
 * https://en.wikipedia.org/wiki/Tie_(music)
 * In music notation, a tie is a curved line connecting the heads of two notes of the same pitch,
 * indicating that they are to be played as a single note with a duration equal to the sum of the individual notes' values.
 * @param notes - array of all notes in the song/segment
 */
function createDurationsForNoteTies(notes: Note[]){
  notes.filter(n => n.tieEnd).forEach(n => setTieDurationForTieBeginNoteAndHaveNotesReferenceEachOther(n, notes));
}

/**
 * Find the note at the beginning of the tie, calculate and set it's durationWithTie property.
 * NOTE: tieBeginNote's durationWithTie property will get set with different values as each new tieEndNote is discovered/iterated. The last time it is set will be the correct value.
 * @param tieEndNote - the index of the tieEnd note in the notes array.
 * @param notes - array of all notes in the song/segment
 */
function setTieDurationForTieBeginNoteAndHaveNotesReferenceEachOther(tieEndNote: Note, notes: Note[]){
  const tieNotes = findPrecedingNotesThatBelongToTie(tieEndNote, notes);
  if(tieNotes.length <= 0){ return logger.vendorSoundSlice.error({message: `could not find a tie begin for tieEnd note: ` + tieEndNote}); }
  const tieBeginNote = tieNotes[0];
  tieBeginNote.tieEndNote = tieEndNote;
  tieEndNote.tieBeginNote = tieBeginNote;
  const durations = tieEndNote.duration + tieNotes.reduce((sum, n) => sum + n.duration, 0);
  tieBeginNote.durationWithTie = durations;
  //assign previous note to each note in the tie (minus the first note) so we can reference it when scoring.
  tieEndNote.previousTieNote = tieNotes[tieNotes.length - 1];
  for(let i = tieNotes.length - 1; i > 0; --i){
    const note = tieNotes[i];
    const previousNote = tieNotes[i - 1];
    if(previousNote){
      note.previousTieNote = previousNote;
    }
  }
}

/**
 * Given a tie end note, finds and returns all notes preceding the tie end note which are part of the tie.
 * Since multiple ties can be chained together, it is not enough to for a single previous note as the beginning of the tie.
 * We must recursively find all previous notes with the same midi
 * @param tieEndNote - we will evaluate notes previous to this note, in order to find all notes that belong to the tie.
 * @param notes - array of all notes in the song/segment.
 */
function findPrecedingNotesThatBelongToTie(tieEndNote: Note, notes: Note[]): Note[]{
  const noteIndex = notes.indexOf(tieEndNote);
  const midi = tieEndNote.midi;
  const track = tieEndNote.path.trackIndex;
  for(let i = noteIndex - 1; i >= 0; --i){
    const possibleTieBeginNote = notes[i];
    if(possibleTieBeginNote.midi === midi && possibleTieBeginNote.path.trackIndex === track){
      //if the possibleTieBeginNote.tieEnd is true, that means that it is not the beginning of the tie (in terms of duration), so recursively iterate backwards until we get to the beginning of the tie.
      if(possibleTieBeginNote.tieEnd){
        return findPrecedingNotesThatBelongToTie(possibleTieBeginNote, notes).concat([possibleTieBeginNote]);
      }else{
        return [possibleTieBeginNote];
      }
    }
  }
  return [];
}
